import { BOOTSTRAP_QUERY_STALE_TIME, queryClient, SHELL_GATE_QUERY_DEFAULTS } from '@/utils/reactQueryClient';
import type { AnnouncementStatus, SDKResult } from '@saas/api-client';
import { createAdminChainController, createAnnouncementController, createChainController } from '@saas/api-client';
import { track } from '@/lib/analytics/track';
import { globalAuthRequestFunction } from '../http/request';
import { makeMutation, makeQuery } from '../http/queryUtils';
import type { AnnouncementListData } from '../types/sdk-helpers';

const chainController = createChainController(globalAuthRequestFunction);
const adminChainController = createAdminChainController(globalAuthRequestFunction);
const announcementController = createAnnouncementController(globalAuthRequestFunction);

// ── FormData helpers (multipart announcements with attachments) ────────────
// The typed SDK's CreateAnnouncementDto/UpdateAnnouncementDto don't include
// the optional file field (backend accepts it via FileInterceptor), so we
// route multipart requests through the same RequestFn the SDK uses. The
// underlying client passes FormData through untouched (see SDK client.ts).

type AnnouncementEntity = AnnouncementListData['data'][number];

type CreateAnnouncementWithFileVariables = {
	title: string;
	body: string;
	status: AnnouncementStatus;
	file?: File | null;
};

type UpdateAnnouncementWithFileVariables = {
	id: string;
	title: string;
	body: string;
	file?: File | null;
};

function buildAnnouncementFormData(values: {
	title: string;
	body: string;
	status?: AnnouncementStatus;
	file?: File | null;
}): FormData {
	const formData = new FormData();
	formData.append('title', values.title);
	formData.append('body', values.body);
	if (values.status) formData.append('status', values.status);
	if (values.file) formData.append('file', values.file);
	return formData;
}

async function createAnnouncementWithFile(
	variables: CreateAnnouncementWithFileVariables,
): Promise<SDKResult<AnnouncementEntity>> {
	return globalAuthRequestFunction<AnnouncementEntity>('POST', '/announcements', {
		body: buildAnnouncementFormData(variables),
	});
}

async function updateAnnouncementWithFile(
	variables: UpdateAnnouncementWithFileVariables,
): Promise<SDKResult<AnnouncementEntity>> {
	const { id, ...rest } = variables;
	return globalAuthRequestFunction<AnnouncementEntity>('PATCH', '/announcements/{id}', {
		params: { id },
		body: buildAnnouncementFormData(rest),
	});
}

// --- Keys factory ---

export const chainServiceKeys = {
	base: 'CHAIN',
	members: (params: Parameters<typeof chainController.getMembers>[0]) =>
		[chainServiceKeys.base, 'members', params] as const,
	chainInfo: (params: Parameters<typeof chainController.getChainInfo>[0]) =>
		[chainServiceKeys.base, 'chain-info', params] as const,
	aggregatedCounts: (params: Parameters<typeof chainController.getAggregatedCounts>[0]) =>
		[chainServiceKeys.base, 'aggregated-counts', params] as const,
	announcements: (params: Parameters<typeof announcementController.findAll>[0]) =>
		[chainServiceKeys.base, 'announcements', params] as const,
	announcement: (params: Parameters<typeof announcementController.findOne>[0]) =>
		[chainServiceKeys.base, 'announcement', params] as const,
};

// --- Invalidation map ---

const invalidateChainQueries = {
	all: () => queryClient.invalidateQueries({ queryKey: [chainServiceKeys.base] }),
	members: () => queryClient.invalidateQueries({ queryKey: [chainServiceKeys.base, 'members'] }),
	chainInfo: () => queryClient.invalidateQueries({ queryKey: [chainServiceKeys.base, 'chain-info'] }),
	aggregatedCounts: () => queryClient.invalidateQueries({ queryKey: [chainServiceKeys.base, 'aggregated-counts'] }),
	announcements: () => queryClient.invalidateQueries({ queryKey: [chainServiceKeys.base, 'announcements'] }),
};

// --- Queries ---

const useGetMembers = makeQuery({
	queryFunction: chainController.getMembers,
	queryKeyFactory: (params) => [...chainServiceKeys.members(params)],
});

// Chain membership and its counts are read on every authenticated boot (`useChainStatus` decides
// which dashboard variant renders), so both are part of the t=0 bootstrap prefetch and pin the
// bootstrap staleness rather than the global `staleTime: 0`.
const useGetChainInfo = makeQuery({
	queryFunction: chainController.getChainInfo,
	queryKeyFactory: (params) => [...chainServiceKeys.chainInfo(params)],
	defaultOptions: SHELL_GATE_QUERY_DEFAULTS,
});

const useGetAggregatedCounts = makeQuery({
	queryFunction: chainController.getAggregatedCounts,
	queryKeyFactory: (params) => [...chainServiceKeys.aggregatedCounts(params)],
	defaultOptions: SHELL_GATE_QUERY_DEFAULTS,
});

// The chain-home announcements card is prefetched in the dashboard loader, so it pins the bootstrap
// staleness to land on that entry on mount. Publishing an announcement invalidates this key.
const useGetAnnouncements = makeQuery({
	queryFunction: announcementController.findAll,
	queryKeyFactory: (params) => [...chainServiceKeys.announcements(params)],
	defaultOptions: { staleTime: BOOTSTRAP_QUERY_STALE_TIME },
});

const useGetAnnouncement = makeQuery({
	queryFunction: announcementController.findOne,
	queryKeyFactory: (params) => [...chainServiceKeys.announcement(params)],
});

// --- Mutations ---

const useCreateMembership = makeMutation({
	mutationFunction: chainController.createMembership,
	onSuccess: () => {
		invalidateChainQueries.members();
		invalidateChainQueries.aggregatedCounts();
	},
});

const useUpdateMembership = makeMutation({
	mutationFunction: adminChainController.updateMember,
	onSuccess: () => {
		invalidateChainQueries.members();
	},
});

const useDeactivateMembership = makeMutation({
	mutationFunction: adminChainController.removeMember,
	onSuccess: () => {
		invalidateChainQueries.members();
		invalidateChainQueries.aggregatedCounts();
	},
});

const useCreateAnnouncement = makeMutation({
	mutationFunction: announcementController.create,
	onSuccess: () => {
		invalidateChainQueries.announcements();
	},
});

const useUpdateAnnouncement = makeMutation({
	mutationFunction: announcementController.update,
	onSuccess: () => {
		invalidateChainQueries.announcements();
	},
});

const usePublishAnnouncement = makeMutation({
	mutationFunction: announcementController.publish,
	onSuccess: (_variables, announcement) => {
		track({ event: 'chain:announcement_published', properties: { announcement_id: announcement.id } });
		invalidateChainQueries.announcements();
	},
});

const useArchiveAnnouncement = makeMutation({
	mutationFunction: announcementController.archive,
	onSuccess: () => {
		invalidateChainQueries.announcements();
	},
});

const useNotifyAnnouncement = makeMutation({
	mutationFunction: announcementController.notify,
	onSuccess: () => {
		invalidateChainQueries.announcements();
	},
});

const useNotifyPreviewAnnouncement = makeMutation({
	mutationFunction: announcementController.notifyPreview,
});

const useCreateAnnouncementWithFile = makeMutation({
	mutationFunction: createAnnouncementWithFile,
	onSuccess: () => {
		invalidateChainQueries.announcements();
	},
});

const useUpdateAnnouncementWithFile = makeMutation({
	mutationFunction: updateAnnouncementWithFile,
	onSuccess: () => {
		invalidateChainQueries.announcements();
	},
});

// --- Export ---

export const chainService = {
	useGetMembers,
	useGetChainInfo,
	useGetAggregatedCounts,
	useGetAnnouncements,
	useGetAnnouncement,
	useCreateMembership,
	useUpdateMembership,
	useDeactivateMembership,
	useCreateAnnouncement,
	useUpdateAnnouncement,
	useCreateAnnouncementWithFile,
	useUpdateAnnouncementWithFile,
	usePublishAnnouncement,
	useArchiveAnnouncement,
	useNotifyAnnouncement,
	useNotifyPreviewAnnouncement,
	keys: chainServiceKeys,
	invalidate: invalidateChainQueries,
};
